import React, { useState, useEffect } from 'react';
import { Button, Input, message, Radio, DatePicker, Modal, Progress } from 'antd';
import { 
  SearchOutlined, 
  AppstoreOutlined, 
  UnorderedListOutlined, 
  ClearOutlined,
  FolderOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTemplate } from '../../utils/printerUtils';
import html2canvas from 'html2canvas';
import { fetchAirwayBills, assignAirwayBillToBatch, createSimpleAirwayBill } from '../../services/airwayBillService';
import { fetchBatches as fetchBatchesService, fetchBatchOrders as fetchBatchOrdersService, deleteBatch as deleteBatchService, createBulkBatch, exportBatch, addOrdersToBatch } from '../../services/batchService';
import BatchIconView from './batch/BatchIconView';
import BatchTable from './batch/BatchTable';
import BatchAssignModal from './batch/BatchAssignModal';
import BatchCreateModal from './batch/BatchCreateModal';
import OrderList from '../orders/OrderList';
import OrderDetailModal from '../orders/OrderDetailModal';
import NavigationBreadcrumb from '../common/NavigationBreadcrumb';

const { RangePicker } = DatePicker;
const { confirm } = Modal;

const BatchManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = location.state?.userId;

  const [allBatches, setAllBatches] = useState([]); // Store all batches
  const [loading, setLoading] = useState(false);
  const [batchSearchText, setBatchSearchText] = useState('');
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [filteredBatches, setFilteredBatches] = useState([]); // Store filtered batches
  const [dateRange, setDateRange] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isViewingOrders, setIsViewingOrders] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [airwayBills, setAirwayBills] = useState([]);
  const [selectedAirwayBill, setSelectedAirwayBill] = useState(null);
  const [assigningBatchId, setAssigningBatchId] = useState(null);
  const [newAirwayBillNumber, setNewAirwayBillNumber] = useState('');
  const [isBulkBatchModalVisible, setIsBulkBatchModalVisible] = useState(false);
  const [bulkBatchNumber, setBulkBatchNumber] = useState('');
  const [bulkOrderIds, setBulkOrderIds] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [viewMode, setViewMode] = useState('icon');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [batchToDownload, setBatchToDownload] = useState(null);
  const [addOrdersModalVisible, setAddOrdersModalVisible] = useState(false);
  const [orderInputText, setOrderInputText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch all batches without search filter
  const fetchBatches = async (params = {}) => {
    setLoading(true);
    try {
      const data = await fetchBatchesService({
        ...params,
        start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
        end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
        user_id: userId,
      });
      setAllBatches(data.results || []);
      setFilteredBatches(data.results || []); // Initialize filtered batches with all batches
      setPagination({
        ...pagination,
        total: data.count,
      });
    } catch (error) {
      console.error('Error fetching batches:', error);
      message.error('获取批次列表失败');
    }
    setLoading(false);
  };

  // Filter batches based on search text and customer
  useEffect(() => {
    // Debug log to see batch structure
    console.log('First batch:', allBatches[0]);
    
    // Ensure search texts are strings
    const batchSearch = (batchSearchText || '').trim().toLowerCase();
    const customerSearch = (customerSearchText || '').trim().toLowerCase();
    
    console.log('Search criteria:', { batchSearch, customerSearch });
    
    // Filter batches
    const results = allBatches.filter(batch => {
      if (!batch) return false;
      
      // Get batch number
      const batchNumber = (batch.batch_number || '').toString().toLowerCase();
      
      // Check batch number match
      const batchMatch = !batchSearch || batchNumber.includes(batchSearch);
      
      // For customer search, check all string values in the batch object
      const customerMatch = !customerSearch || Object.values(batch)
        .some(value => {
          if (!value) return false;
          const strValue = value.toString().toLowerCase();
          return strValue.includes(customerSearch.toLowerCase());
        });
      
      return batchMatch && customerMatch;
    });
    
    console.log('Filtered results:', results);
    
    setFilteredBatches(results);
    setPagination(prev => ({
      ...prev,
      total: results.length,
      current: 1 // Reset to first page when filtering
    }));
  }, [batchSearchText, customerSearchText, allBatches]);

  const loadAirwayBills = async () => {
    try {
      setLoading(true);
      const data = await fetchAirwayBills();
      setAirwayBills(data.results || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading airway bills:', error);
      message.error('获取空运单列表失败');
    }
  };

  const fetchOrdersForBatch = async (batchId) => {
    setLoading(true);
    try {
      const response = await fetchBatchOrdersService(batchId);
      console.log('Batch orders response:', response);
      
      let ordersData = [];
      if (Array.isArray(response)) {
        ordersData = response;
      } else if (response && response.results) {
        ordersData = response.results;
      }
      
      console.log('Setting orders:', ordersData);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders for batch:', error);
      message.error('获取订单列表失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isViewingOrders) {
      fetchBatches(pagination);
    }
  }, [dateRange, isViewingOrders]);

  const handleTableChange = (newPagination, filters, sorter) => {
    fetchBatches({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sort: sorter,
    });
  };

  const handleViewOrders = async (batch) => {
    console.log('Viewing orders for batch:', batch);
    setSelectedBatch(batch);
    try {
      const response = await fetchBatchOrdersService(batch.id);
      console.log('Batch orders response:', response);
      
      let ordersData = [];
      if (Array.isArray(response)) {
        ordersData = response;
      } else if (response && response.results) {
        ordersData = response.results;
      }
      
      console.log('Total orders loaded:', ordersData.length);
      setOrders(ordersData);
      setIsViewingOrders(true);
    } catch (error) {
      console.error('Error fetching orders for batch:', error);
      message.error('获取订单列表失败');
    }
  };

  const handleReturn = () => {
    if (userId) {
      navigate('/admin/customers');
    } else {
      setIsViewingOrders(false);
      setSelectedBatch(null);
      setOrders([]);
    }
  };

  const handleDeleteBatch = async (e, batchId) => {
    if (e) {
      e.stopPropagation();
    }

      confirm({
        title: '确认删除',
        icon: <ExclamationCircleOutlined />,
        content: '确定要删除这个批次吗？此操作不可恢复。',
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        async onOk() {
          try {
            await deleteBatchService(batchId);
            message.success('批次删除成功');
            fetchBatches(pagination);
          } catch (error) {
            console.error('Error deleting batch:', error);
            if (error.response && error.response.status === 403) {
              message.error('没有权限删除此批次');
            } else {
              message.error('删除批次失败');
            }
          }
        },
      });
  };

  const handleAssignAirwayBill = (e, batchId) => {
    e.stopPropagation();
    setAssigningBatchId(batchId);
    setSelectedAirwayBill(null);
    setNewAirwayBillNumber('');
    loadAirwayBills();
    setIsAssignModalVisible(true);
  };

  const handleCreateAndAssignAirwayBill = async () => {
    if (!newAirwayBillNumber.trim()) {
      message.warning('请输入空运单号');
      return;
    }

    try {
      const newAirwayBill = await createSimpleAirwayBill(newAirwayBillNumber.trim());
      await assignAirwayBillToBatch(assigningBatchId, newAirwayBill.id);
      message.success('空运单创建并分配成功');
      setIsAssignModalVisible(false);
      setNewAirwayBillNumber('');
      fetchBatches(pagination);
    } catch (error) {
      console.error('Error creating and assigning airway bill:', error);
      message.error('空运单创建或分配失败');
    }
  };

  const handleAssignModalOk = async () => {
    if (newAirwayBillNumber) {
      await handleCreateAndAssignAirwayBill();
      return;
    }

    if (!selectedAirwayBill) {
      message.warning('请选择空运单或创建新空运单');
      return;
    }

    try {
      await assignAirwayBillToBatch(assigningBatchId, selectedAirwayBill);
      message.success('空运单分配成功');
      setIsAssignModalVisible(false);
      setSelectedAirwayBill(null);
      fetchBatches(pagination);
    } catch (error) {
      console.error('Error assigning airway bill:', error);
      message.error('空运单分配失败');
    }
  };

  const handleBulkBatchSubmit = async () => {
    if (!bulkBatchNumber.trim() || !bulkOrderIds.trim() || !selectedCustomer) {
      message.warning('请输入批次号、订单号和选择客户');
      return;
    }

    try {
      const orderIdList = bulkOrderIds.split('\n').map(id => id.trim()).filter(id => id);
      await createBulkBatch(bulkBatchNumber, orderIdList, selectedCustomer);
      message.success('批量添加成功');
      setIsBulkBatchModalVisible(false);
      setBulkBatchNumber('');
      setBulkOrderIds('');
      fetchBatches(pagination);
    } catch (error) {
      console.error('Error submitting bulk batch:', error);
      message.error('批量添加失败');
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOrderModalVisible(true);
  };

  const handleClear = () => {
    setBatchSearchText('');
    setCustomerSearchText('');
    setDateRange(null);
    fetchBatches({
      ...pagination,
      current: 1
    });
  };

  const getBreadcrumbItems = () => {
    const items = [];
    
    if (userId) {
      items.push({
        label: '客户列表',
        icon: <TeamOutlined style={{ marginRight: '8px' }} />,
        onClick: handleReturn
      });
    }
    
    items.push({
      label: '批次管理',
      icon: <FolderOutlined style={{ marginRight: '8px' }} />,
      onClick: isViewingOrders ? () => setIsViewingOrders(false) : undefined
    });

    if (isViewingOrders && selectedBatch) {
      items.push({
        label: `批次 ${selectedBatch.batch_number}`,
      });
    }

    return items;
  };

  const generateLabel = async (order) => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.width = '100mm';
      tempDiv.style.height = '150mm';
      tempDiv.style.padding = '0';
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.boxSizing = 'border-box';

      let template;
      try {
        template = getTemplate(order.courier_service, order.port);
      } catch (error) {
        console.error('Template selection failed:', error);
        message.error(error.message);
        return null;
      }

      const orderWithCorrectAddress = {
        ...order,
        recipient_name: order.label_address ? order.label_recipient : order.recipient_name,
        recipient_phone: order.label_address ? order.label_phone : order.recipient_phone,
        recipient_address: order.label_address ? order.label_address : order.recipient_address
      };
      const labelHtml = await template(orderWithCorrectAddress);
      if (!labelHtml) {
        message.error('面单生成失败');
        return null;
      }

      tempDiv.innerHTML = labelHtml;
      document.body.appendChild(tempDiv);

      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(tempDiv, {
        scale: 4,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 378,
        height: 567,
        onclone: (clonedDoc) => {
          const clonedDiv = clonedDoc.querySelector('div');
          if (clonedDiv) {
            clonedDiv.style.width = '378px';
            clonedDiv.style.height = '567px';
          }
        }
      });

      document.body.removeChild(tempDiv);
      return canvas;
    } catch (error) {
      console.error('Label generation error:', error);
      message.error('面单生成失败');
      return null;
    }
  };

  const handleDownloadLabels = async () => {
    setBatchToDownload(selectedBatch);
    setDownloadModalVisible(true);
  };

  const handleConfirmDownload = async () => {
    setIsGenerating(true);
    setDownloadProgress(0);

    try {
      // Get all orders for the batch
      const response = await fetchBatchOrdersService(batchToDownload.id);
      const orders = Array.isArray(response) ? response : response.results || [];
      const totalOrders = orders.length;
      const labelCanvases = [];

      // Generate labels with progress updates
      for (let i = 0; i < orders.length; i++) {
        const canvas = await generateLabel(orders[i]);
        if (canvas) {
          labelCanvases.push({
            canvas,
            orderNumber: orders[i].order_number
          });
        }
        setDownloadProgress(Math.round(((i + 1) / totalOrders) * 100));
      }

      if (labelCanvases.length === 0) {
        throw new Error('No labels were generated successfully');
      }

      // Convert canvases to PDFs and create zip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add each label as a JPEG to the zip
      labelCanvases.forEach(({ canvas, orderNumber }) => {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        const base64Data = dataUrl.split(',')[1];
        zip.file(`${orderNumber}.jpg`, base64Data, { base64: true });
      });

      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `批次${batchToDownload.batch_number}_面单.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('面单下载成功');
    } catch (error) {
      console.error('Download error:', error);
      message.error('面单下载失败');
    } finally {
      setIsGenerating(false);
      setDownloadModalVisible(false);
      setDownloadProgress(0);
      setBatchToDownload(null);
    }
  };

  const handleExportBatch = async () => {
    try {
      await exportBatch(selectedBatch);
      message.success('批次导出成功');
    } catch (error) {
      console.error('Error exporting batch:', error);
      message.error('批次导出失败');
    }
  };

  const handleAddOrdersSubmit = async () => {
    if (!orderInputText.trim()) {
      message.warning('请输入订单号或快递单号');
      return;
    }

    try {
      const orderIdList = orderInputText.split('\n').map(id => id.trim()).filter(id => id);
      await addOrdersToBatch(selectedBatch.batch_number, orderIdList, selectedBatch.customer);
      message.success(`成功添加 ${orderIdList.length} 个订单到批次`);
      setAddOrdersModalVisible(false);
      setOrderInputText('');
      // Refresh the orders list
      await fetchOrdersForBatch(selectedBatch.id);
    } catch (error) {
      console.error('Error adding orders to batch:', error);
      message.error('添加订单失败');
    }
  };

  if (isViewingOrders) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <NavigationBreadcrumb items={getBreadcrumbItems()} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
              icon={<PlusOutlined />} 
              onClick={() => setAddOrdersModalVisible(true)}
            >
              添加订单
            </Button>
            <Button 
              icon={<FilePdfOutlined />} 
              onClick={handleDownloadLabels}
            >
              下载PDF
            </Button>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              onClick={handleExportBatch}
            >
              导出批次
            </Button>
          </div>
        </div>

        <OrderList
          orders={orders}
          loading={loading}
          onOrderClick={handleOrderClick}
          pagination={false}
        />

        <OrderDetailModal
          visible={orderModalVisible}
          order={selectedOrder}
          onClose={() => setOrderModalVisible(false)}
        />

        <Modal
          title="下载面单"
          open={downloadModalVisible}
          onOk={handleConfirmDownload}
          onCancel={() => {
            setDownloadModalVisible(false);
            setBatchToDownload(null);
          }}
          okText="确认"
          cancelText="取消"
          okButtonProps={{ disabled: isGenerating }}
          cancelButtonProps={{ disabled: isGenerating }}
        >
          {isGenerating ? (
            <div>
              <p>正在生成面单，请稍候...</p>
              <Progress percent={downloadProgress} status="active" />
            </div>
          ) : (
            <p>确定下载此批次的所有面单PDF文件吗？</p>
          )}
        </Modal>

        <Modal
          title={`添加订单到批次 ${selectedBatch?.batch_number}`}
          open={addOrdersModalVisible}
          onOk={handleAddOrdersSubmit}
          onCancel={() => {
            setAddOrdersModalVisible(false);
            setOrderInputText('');
          }}
          okText="添加"
          cancelText="取消"
          width={600}
        >
          <div style={{ marginBottom: '16px' }}>
            <p style={{ marginBottom: '8px', color: '#666' }}>
              请输入订单号或快递单号，每行一个：
            </p>
            <Input.TextArea
              value={orderInputText}
              onChange={(e) => setOrderInputText(e.target.value)}
              placeholder="例如：&#10;ORDER001&#10;SF1234567890&#10;ORDER002"
              rows={8}
              style={{ resize: 'vertical' }}
            />
            <p style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
              支持订单号和快递单号混合输入，系统会自动识别并添加到当前批次
            </p>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <div>
      <NavigationBreadcrumb items={getBreadcrumbItems()} />
      
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        background: '#fff',
        padding: '16px 0px',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
          <Input
            placeholder="输入批次号搜索"
            style={{ maxWidth: 200 }}
            value={batchSearchText}
            onChange={(e) => setBatchSearchText(e.target.value)}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          />
          <Input
            placeholder="输入客户搜索"
            style={{ maxWidth: 200 }}
            value={customerSearchText}
            onChange={(e) => setCustomerSearchText(e.target.value)}
            prefix={<TeamOutlined style={{ color: '#bfbfbf' }} />}
          />
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: 240 }}
          />
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            清除
          </Button>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Button
            type="primary"
            onClick={() => setIsBulkBatchModalVisible(true)}
          >
            新增批次号
          </Button>
          <Radio.Group 
            value={viewMode} 
            onChange={(e) => setViewMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="icon">
              <AppstoreOutlined /> 图标
            </Radio.Button>
            <Radio.Button value="list">
              <UnorderedListOutlined /> 列表
            </Radio.Button>
          </Radio.Group>
        </div>
      </div>
      
      {viewMode === 'list' ? (
        <BatchTable
          batches={filteredBatches}
          loading={loading}
          pagination={pagination}
          onTableChange={handleTableChange}
          onViewOrders={handleViewOrders}
          onAssignAirwayBill={handleAssignAirwayBill}
          onDeleteBatch={handleDeleteBatch}
        />
      ) : (
        <BatchIconView
          batches={filteredBatches}
          onViewOrders={handleViewOrders}
          onAssignAirwayBill={handleAssignAirwayBill}
          onDeleteBatch={handleDeleteBatch}
        />
      )}

      <BatchAssignModal
        visible={isAssignModalVisible}
        onCancel={() => {
          setIsAssignModalVisible(false);
          setSelectedAirwayBill(null);
          setNewAirwayBillNumber('');
        }}
        onOk={handleAssignModalOk}
        loading={loading}
        airwayBills={airwayBills}
        selectedAirwayBill={selectedAirwayBill}
        newAirwayBillNumber={newAirwayBillNumber}
        onAirwayBillSelect={(value) => {
          setSelectedAirwayBill(value);
          setNewAirwayBillNumber('');
        }}
        onNewAirwayBillChange={(value) => {
          setNewAirwayBillNumber(value);
          setSelectedAirwayBill(null);
        }}
        onCreateAndAssign={handleCreateAndAssignAirwayBill}
      />

      <BatchCreateModal
        visible={isBulkBatchModalVisible}
        onCancel={() => {
          setIsBulkBatchModalVisible(false);
          setBulkBatchNumber('');
          setBulkOrderIds('');
          setSelectedCustomer(null);
        }}
        onOk={handleBulkBatchSubmit}
        batchNumber={bulkBatchNumber}
        orderIds={bulkOrderIds}
        onBatchNumberChange={setBulkBatchNumber}
        onOrderIdsChange={setBulkOrderIds}
        selectedCustomer={selectedCustomer}
        onCustomerChange={setSelectedCustomer}
      />
    </div>
  );
};

export default BatchManagement;
